import { computed, readonly, Ref, ref, watch } from 'vue';
import {
    AcceptCustomerOfferConsentRequest,
    BasketInvoiceAddress,
    ClientResponse,
    ClientResponseOfCreateConsentResponse,
    CreateCustomerOfferConsentRequest,
    CreateCustomerOfferRequest,
    CustomerOfferModel,
    PrintCustomerOfferByIdRequest,
    STOREClientService,
} from '@/storecontext/shared/api/server-apis';
import useBasket from '@/storecontext/basket/useBasket';
import utilitiesService from '@/shared/utilities.service';
export const offerMessageId = 'offerMessageId';

const state: Ref<CustomerOfferModel> = ref({} as CustomerOfferModel);
const editMode: Ref<boolean> = ref(false);

export function useCustomerOffer() {
    const { createCustomerOfferAndFetchNewBasket, updateCustomerOfferAndFetchNewBasket, basketId, basket } = useBasket();

    const customerOfferId = computed<string | undefined>(() => basket.value.customerOfferId);
    const canCreateNewOffer = computed<boolean>(() => !editMode.value && basket.value.canCreateCustomerOffer);

    const createOrUpdate = async(payload: CreateCustomerOfferRequest, update: boolean = false): Promise<void> => {
        let response;

        if (update) {
            response = await updateCustomerOfferAndFetchNewBasket(payload, offerMessageId);
        } else {
            response = await createCustomerOfferAndFetchNewBasket(payload, offerMessageId);
        }
        state.value = response;
    };

    const acceptCustomerOfferConsentRequest = async(consentRequestId: string, messageId?: string): Promise<ClientResponse> => {
        const payload: AcceptCustomerOfferConsentRequest = {
            consentRequestId: consentRequestId,
        };

        return await STOREClientService.acceptCustomerOfferConsentRequest(payload, messageId);
    };

    const createCustomerOfferConsentRequest = async(offerConsentUrl: string, email: string, messageId?: string): Promise<ClientResponseOfCreateConsentResponse> => {
        const payload: CreateCustomerOfferConsentRequest = {
            basketId: basketId.value,
            emailAddress: email,
            imageSizeInPixels: 200,
            targetPageUrl: offerConsentUrl,
        };

        return await STOREClientService.createCustomerOfferConsentRequest(payload, messageId);
    };

    const printCustomerOfferById = async(payload: PrintCustomerOfferByIdRequest, messageId?: string): Promise<void> => {
        await STOREClientService.printCustomerOfferById(payload, messageId);
    };

    const getAddress = (): Promise<BasketInvoiceAddress | undefined> => {
        return STOREClientService.getBasketInvoiceAddress(basketId.value)
            .then(utilitiesService.unwrapModel);
    };

    const setEditMode = (newValue: boolean): void => {
        editMode.value = newValue;
    };

    watch(customerOfferId, (id) => {
        setEditMode(id !== undefined);
    });

    return {
        state: readonly(state),
        editMode: readonly(editMode),
        canCreateNewOffer,
        customerOfferId,
        setEditMode,
        createOrUpdate,
        acceptCustomerOfferConsentRequest,
        createCustomerOfferConsentRequest,
        getAddress,
        printCustomerOfferById,
    };
}
